import { List, Map } from 'immutable';
import add from 'date-fns/add';

import type { ImmutableTrip, ImmutableTrips, Trip } from 'src/types/trips/Trips';
import type { ImmutableOffer, Offer } from 'src/types/offer/Offer';
import type { ImmutableBoat, ImmutableBoats } from 'src/types/boat/BoatDetail';
import isMobile from 'src/common/utils/isMobile';
import { PATHS, USER_ROLES } from '../common/constants';
import {
  BOAT_FIELD,
  GUESTS_FIELD,
  MESSAGE_BLACKLIST_STATES,
  TRIP_STATES,
  CAPTAINED_FIELD,
  TRIP_END_DATE_FIELD_UTC,
  CAPTAIN_COST_INCLUDED_FIELD,
  CAPTAIN_OPTION_IS_AUTO_PATCHED_FIELD,
  GUEST_FIELDS,
} from './constants';
import { appendRoute, preserveSearch, siblingRoute } from '../common/utils/routing';
import reviewRoutes from './components/pages/Complete/reviewsConfig';

const { OWNER, RENTER } = USER_ROLES;

type TripUserRole = typeof OWNER | typeof RENTER;
type GetUserRoleFunction = (trip: ImmutableTrip) => TripUserRole | undefined;
export const getUserRole: GetUserRoleFunction = trip => trip?.getIn(['state', 'role']);

export const getOtherParty = (trip: ImmutableTrip) => {
  const userRole = getUserRole(trip);
  return (userRole === OWNER ? trip.get(RENTER) : trip.get(OWNER)) || Map();
};

// Decide whether any of a set of fields has been modified on the trip.
export const modified = (
  trip: ImmutableTrip,
  // If `fields` arg isn't provided, check against all trip fields and field comments
  fields = Object.keys((trip.get('modified') || List()).toJS()),
) => fields.some(field => trip.getIn(['modified', field]));

/** For Date, Time, Duration cards, DateTimeDuration selector returns is modified values */
export const offerModified = (
  offer: ImmutableOffer,
  trip: ImmutableTrip,
  fields: string[],
) => offer && fields.some(field => {
  const offerValue = offer.get(field);
  const tripValue = trip.get(field);
  if (field === BOAT_FIELD) {
    return offerValue && offerValue.get('id') !== tripValue.get('id');
  }

  if (GUEST_FIELDS.includes(field)) {
    const { adults, children, seniors, infants } = offer.toJS();
    const offerObj = { adults, children, seniors, infants };
    const { total, ...tripObj } = trip.get(GUESTS_FIELD).toJS();
    return offerValue && JSON.stringify(offerObj) !== JSON.stringify(tripObj);
  }

  if (field === CAPTAINED_FIELD) {
    return offer && offer.has('captained') && offerValue !== tripValue;
  }

  if (field === 'captain_cost_included') {
    return offer && offer.has('captain_cost_included') && offerValue !== tripValue;
  }

  return (
    offer.has(field)
    && offerValue !== null
    && offerValue !== ''
    && offerValue !== tripValue
  );
});

export const allowMessages = (state:string) => !MESSAGE_BLACKLIST_STATES.includes(state);

export const guestsString = (totalGuests: number) => `${totalGuests} ${totalGuests > 1 ? 'People' : 'Person'}`;

export const showContactInfoWarning = (trip: ImmutableTrip, field: string) => trip.get('obfuscated', List()).includes(field);

export const isUserSuspended = (trip: ImmutableTrip) => trip.getIn(['state', 'state']) === TRIP_STATES.THEY_BEEN_SUSPENDED;

export const isUserBanned = (trip: ImmutableTrip) => trip.getIn(['state', 'state']) === TRIP_STATES.THEY_BEEN_BANNED;

export const userIsOwner = (trip: ImmutableTrip) => getUserRole(trip) === OWNER;
export const userIsRenter = (trip: ImmutableTrip) => getUserRole(trip) === RENTER;

export const getReviewUrl = (page, role, direction, location) => {
  const { pathname } = location;
  const segment = reviewRoutes[role][page][direction];
  const route = segment && siblingRoute(pathname, segment);

  return route && preserveSearch(route, location);
};

/**
 * Determines if trip should auto-navigate to messages tab.
 * Returns true when there are unread messages but no unseen state changes.
 */
export const shouldRedirectToMessages = (trip: ImmutableTrip): boolean => {
  const hasUnreadMessage = trip.get('has_unread_message');
  const hasUnseenStateChange = trip.get('has_unseen_state_change');

  return Boolean(hasUnreadMessage && !hasUnseenStateChange);
};

const firstTrip = (trips: ImmutableTrips) => trips.get('results')?.first() || Map();

/**
 * Generates the path to the first trip in the inbox.
 * Automatically appends '/messages/' if the trip has unread messages.
 */
const firstTripPath = (getState: () => any): string => {
  const state = getState();
  const trips = state.trips;
  const trip = firstTrip(trips);
  const basePath = appendRoute(PATHS.INBOX, trip.get('pk') || '');
  const messageSuffix = shouldRedirectToMessages(trip) ? 'messages/' : '';

  return `${basePath}${messageSuffix}`;
};

// Yeah, this probably constitutes thunk abuse.
export const firstTripRedirectThunk = (history, location) => (
  // We only want this to happen if there isn't a trip selected.
  (dispatch, getState) => location.pathname === PATHS.INBOX && !isMobile() && history.push(
    preserveSearch(firstTripPath(getState), location),
  ));

export const ordinalSuffix = (strings, ...values) => {
  let suffixedNumber = values[1];

  switch (suffixedNumber) {
    case 0:
      suffixedNumber = '';
      break;
    case 1:
      suffixedNumber += 'st';
      break;
    case 2:
      suffixedNumber += 'nd';
      break;
    case 3:
      suffixedNumber += 'd';
      break;
    default:
    // Nothing
  }

  return `${values[0]}${strings[1]} ${suffixedNumber} ${values[2]} ${strings[3]}`;
};

// The logic here is that if the renter or owner is
// pissed off, we won't invite them to leave a public
// review, so we put logic in place to skip
// the public review step after the private note step.
export const isNegativeReview = (rating: number, recommendation: boolean) => (
  rating < 3 || recommendation === false
);

export const selectTrip = (trips: ImmutableTrips, tripId: string | number): ImmutableTrip => trips
  .get('results')
  .find(result => result?.get('pk') === Number(tripId))
  || Map();

/**
 * Get specific trip value from the offer if there are unsent changes;
 * otherwise, get it from the trip.
 */
export const getTripValue = <Field extends keyof (Offer | Trip)>(
  // eslint-disable-next-line @typescript-eslint/default-param-last
  offer: ImmutableOffer = Map() as ImmutableOffer,
  trip: ImmutableTrip,
  fieldName: Field,
) => offer.get(fieldName)
    // Fallback to trip value
    ?? trip.get(fieldName);

// eslint-disable-next-line @typescript-eslint/default-param-last
export const roundToNextMinutes = (date = new Date(), { roundToNext = 15 }): Date => {
  const minutes = date.getMinutes();
  const roundedMinutes = Math.ceil(minutes / roundToNext) * roundToNext;

  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    roundedMinutes,
  );
};

/**
 * Get selected boat from the offer if it was edited or from the trip
 */
export const getSelectedBoat = (
  // eslint-disable-next-line @typescript-eslint/default-param-last
  offer: ImmutableOffer = Map() as ImmutableOffer,
  trip: ImmutableTrip,
  userBoats: ImmutableBoats,
): ImmutableBoat => {
  const offerBoatId = offer.getIn([BOAT_FIELD, 'id']);
  const tripBoatId = trip.getIn([BOAT_FIELD, 'id']);
  // If owner selects other boat than boat linked to trip
  return offerBoatId && offerBoatId !== tripBoatId
    // We find and use that boat
    ? userBoats.find(boat => boat?.get('id') === offerBoatId)
    : trip.get(BOAT_FIELD);
};

/**
 * Checks if the current date is less than two days since the end date of the trip
 * and if the trip is completed.
 *
 * @param trip - The immutable trip map.
 * @returns Returns `true` if the current date is more than two days since
 * the trip end date, `false` otherwise.
 */
export const isChargeEligible = (trip: ImmutableTrip): boolean => {
  const tripEndDate = trip.get(TRIP_END_DATE_FIELD_UTC);
  const today = new Date();
  const dateThreshold = add(new Date(tripEndDate), { days: 2 });
  return today <= dateThreshold;
};

/**
 * Checks if an offer is considered empty.
 *
 * An offer is considered empty if it has no fields after excluding certain fields
 * related to auto-patching. Specifically, if the offer has been auto-patched (indicated
 * by the `CAPTAIN_OPTION_IS_AUTO_PATCHED_FIELD`), the fields `CAPTAINED_FIELD`,
 * `CAPTAIN_COST_INCLUDED_FIELD`, and `CAPTAIN_OPTION_IS_AUTO_PATCHED_FIELD` are
 * excluded from the emptiness check. If none of these conditions apply, the offer is
 * checked for emptiness as is.
 *
 * @param offer - The offer to check, expected to be an immutable data structure.
 * @returns - Returns true if the offer is considered empty, otherwise false.
 */
export const offerIsEmpty = (offer: ImmutableOffer): boolean => {
  // If captain option is auto-patched
  if (offer?.get(CAPTAIN_OPTION_IS_AUTO_PATCHED_FIELD)) {
    return offer
      // Exclude auto-patched fields from the check
      ?.delete(CAPTAINED_FIELD)
      .delete(CAPTAIN_COST_INCLUDED_FIELD)
      .delete(CAPTAIN_OPTION_IS_AUTO_PATCHED_FIELD)
      .isEmpty();
  }
  return offer?.isEmpty();
};
